import type { ReactNode } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import type { Recipe } from "@/lib/api/types";
import { useNutrients } from "@/lib/api/nutrients";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FormField,
  emptyToUndef as _emptyToUndef,
} from "@/components/forms/field";

const numString = z
  .string()
  .min(1, "required")
  .refine((v) => !Number.isNaN(Number(v)), "must be a number");

const entrySchema = z.object({
  order_rank: z
    .string()
    .min(1, "required")
    .refine(
      (v) => Number.isInteger(Number(v)) && Number(v) >= 0,
      "non-negative integer",
    ),
  nutrient_id: z.string().min(1, "select a nutrient"),
  ml_per_gal: numString,
  notes: z.string().optional(),
});

export const recipeSchema = z.object({
  name: z.string().min(1, "name is required"),
  description: z.string().optional(),
  entries: z.array(entrySchema),
  reason: z.string().optional(),
});

export type RecipeFormValues = z.infer<typeof recipeSchema>;

export const emptyToUndef = _emptyToUndef;

export interface RecipeFormProps {
  initial?: Recipe;
  submitting: boolean;
  onSubmit: (values: RecipeFormValues) => void;
  banner?: ReactNode;
  submitLabel?: string;
  showReason?: boolean;
}

export function RecipeForm({
  initial,
  submitting,
  onSubmit,
  banner,
  submitLabel,
  showReason = false,
}: RecipeFormProps) {
  const nutrients = useNutrients();
  const form = useForm<RecipeFormValues>({
    resolver: zodResolver(recipeSchema),
    defaultValues: defaultsFor(initial),
  });
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "entries",
  });
  const err = form.formState.errors;

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="space-y-6 max-w-3xl"
      noValidate
    >
      {banner}

      <FormField label="Name" error={err.name?.message}>
        <Input autoFocus {...form.register("name")} />
      </FormField>
      <FormField label="Description">
        <Textarea rows={2} {...form.register("description")} />
      </FormField>

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium">Entries</legend>
        <p className="text-xs text-muted-foreground">
          Order matters: lower order_rank mixes first. Each entry pins one
          nutrient at one dose; gate rules are edited via the API for now.
        </p>
        <div className="space-y-2">
          {fields.map((f, i) => (
            <div
              key={f.id}
              className="grid grid-cols-[80px_1fr_120px_auto] gap-2 items-start"
            >
              <Input
                type="number"
                min={0}
                step={1}
                placeholder="rank"
                {...form.register(`entries.${i}.order_rank` as const)}
              />
              <Select
                value={form.watch(`entries.${i}.nutrient_id`)}
                onValueChange={(v) =>
                  form.setValue(`entries.${i}.nutrient_id`, v, {
                    shouldValidate: true,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="nutrient" />
                </SelectTrigger>
                <SelectContent>
                  {nutrients.data?.items.map((n) => (
                    <SelectItem key={n.id} value={n.id}>
                      {n.name}
                      {n.system_provided ? " (sys)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="number"
                step="any"
                placeholder="ml/gal"
                {...form.register(`entries.${i}.ml_per_gal` as const)}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => remove(i)}
              >
                ×
              </Button>
              {err.entries?.[i] && (
                <p className="col-span-4 text-xs text-destructive">
                  {err.entries[i]?.order_rank?.message ??
                    err.entries[i]?.nutrient_id?.message ??
                    err.entries[i]?.ml_per_gal?.message}
                </p>
              )}
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            append({
              order_rank: String(fields.length),
              nutrient_id: "",
              ml_per_gal: "",
              notes: "",
            })
          }
        >
          Add entry
        </Button>
      </fieldset>

      {showReason && (
        <FormField
          label="Reason for bump"
          help="Captured in the audit log."
        >
          <Input {...form.register("reason")} />
        </FormField>
      )}

      <Button type="submit" disabled={submitting}>
        {submitting ? "Saving…" : (submitLabel ?? "Save")}
      </Button>
    </form>
  );
}

function defaultsFor(r: Recipe | undefined): RecipeFormValues {
  if (!r) {
    return { name: "", description: "", entries: [] };
  }
  return {
    name: r.name,
    description: r.description ?? "",
    entries: r.entries.map((e) => ({
      order_rank: String(e.order_rank),
      nutrient_id: e.nutrient_id,
      ml_per_gal: String(e.ml_per_gal),
      notes: e.notes ?? "",
    })),
  };
}
